<script>

    import dressing from "@intellectability/platform-common";

    // common constants
    import {ACTIONS} from "@intellectability/platform-common/constants";

    import {
        ExportButtons,
        FlexList,
        Icon,
        ChoosePersonModal,
        Modal,
        toast
    } from "@intellectability/platform-common/sv";
    import {actionCheck, create_security_error} from "@intellectability/platform-common/utils";
    import {userSecurity} from "@intellectability/platform-common/sv/state";
    import * as yup from "yup";

    // bootstrap
    import {
        Breadcrumb,
        BreadcrumbItem,
        Card,
        CardBody,
        CardHeader,
        Col,
        Container,
        Row
    } from "@sveltestrap/sveltestrap";

    import {createForm} from "svelte-forms-lib";

    import {fade} from "svelte/transition";

    import CaseNoteFormModal from "./lib/CaseNoteFormModal.svelte";

    let {context, consID = undefined} = $props();

    const {
        CASE_NOTES_GLOBAL_LIST,
        CASE_NOTES_PERSON_LIST,
        CASE_NOTE_EDIT,
        CASE_NOTE_DELETE,
        CASE_NOTE_PDF
    } = ACTIONS;

    let isLoggedIn = $derived(userSecurity.loggedIn);

    let loading = $state(true);
    const hasAccess = $derived(actionCheck(userSecurity.userActions));

    let initialValues = $derived({
        type: "",
        note_date: new Date().toISOString().split("T")[0]
    });

    const form_props = {
        validationSchema: yup.object().shape({
            type: yup.string()
            .required(),
            note_date: yup.date()
            .required()
        })
    };

    const {form, errors, handleSubmit, updateInitialValues} = createForm({
        initialValues,
        validationSchema: form_props.validationSchema,
        onSubmit: () => {}
    });


    let confirmDelete = $state(false);
    let deleteId = $state("");

    // Selected note to be edited
    let selectedNoteId = $state(undefined);
    // Selected person for the new case note
    let selectedConsId = $state(undefined);

    let openCaseNoteModal = $state(false);
    let caseNoteName = $derived(userSecurity.applicationConfig?.case_notes_name || "Case Note");

    // flexlist data
    let caseNotes = $state.raw([]);

    // for dropdowns
    let billableTypes = $state.raw([
        {value: "Y", text: "Yes"},
        {value: "N", text: "No"},
        {value: "-", text: "-"}
    ]);

    let caseNoteTypes = $state.raw([]); // id are arbitrary, non-sensical and unused

    // filter options
    let filterForPerson = $state("");
    let filterForNote_date_fmt = $state("");
    let filterForWritter = $state("");
    let filterForType = $state("");
    let filterForNote = $state("");
    let filterForWrittenOn = $state("");
    let filterForBillable = $state("");

    // action access
    let canManage = $derived(hasAccess(CASE_NOTE_EDIT));
    let canDelete = $derived(hasAccess(CASE_NOTE_DELETE));

    // pagination
    let pagination = $state({
        show: true,
        page: 1,
        limit: 10,
        recordsFiltered: -1,
        recordsTotal: -1,
        pageLengthMenu: [10, 25, 50, 100, 500]
    });

    // sort
    /** @type {{field: string, order: "asc"|"desc"}} */
    let sort = $state({
        field: "name",
        order: "asc"
    });

    // column filters take the string
    let caseNoteTypeOptions = $derived(caseNoteTypes.map(function (type) {
        return {name: type.type, value: type.type};
    }));

    let billableTypeOptions = $derived(billableTypes.map(function (option) {
        return {name: option.text, value: option.value};
    }));

    $effect(() => {
        if (!loading && isLoggedIn) {
            fetch_filter_list();
        }
    });

    $effect(function load() {

        if (!isLoggedIn) {
            return;
        }

        if (!hasAccess(CASE_NOTES_GLOBAL_LIST) && !hasAccess(CASE_NOTES_PERSON_LIST)) {

            const security_error = create_security_error("App.svelte",
                "CaseNoteList", CASE_NOTES_PERSON_LIST);
            dressing.api.report_security_error(security_error)
            .then(() => dressing.api.logout());
        }


        const needed_initial_data = [
            dressing.get("/cm/case_notes/types").then(({data: res}) => {
                const {success, data} = res;
                if (success === true) {
                    caseNoteTypes = data.types;
                    return true;
                }
                return false;
            })
        ];

        Promise.allSettled(needed_initial_data).then(function (resolved) {
            // @ts-ignore
            const success = resolved.every(r => r.value === true);
            if (success) {
                loading = false;
            }
            else {
                toast.error("Error loading initial data");
            }
        });


    });

    function fetch_filter_list() {
        const filter_query = {
            cons_name: filterForPerson || null,
            note_date_fmt: filterForNote_date_fmt || null,
            type: filterForType || null,
            note: filterForNote || null,
            billable: filterForBillable || null,
            usr_name: filterForWritter || null,
            created_fmt: filterForWrittenOn || null
        };

        const sort_query = {
            sort: sort.field || null,
            order: sort.order || null
        };

        const page_query = {
            page: pagination.page || null,
            limit: pagination.limit || null
        };


        dressing.get("/cm/case_notes", {
            params: Object.assign(filter_query, {...sort_query, ...page_query})
        }).then(({data: response}) => {
            const {success, data} = response;
            if (success === true) {
                const {case_notes: array} = data;
                if (data.case_notes) {
                    caseNotes = array;
                    pagination.recordsFiltered = data.recordsFiltered;
                    pagination.recordsTotal = data.recordsTotal;
                }
            }
            loading = false;
        }).catch((error) => {
            loading = false;
            toast.error("Error fetching case notes");
        });
    }

    /**
     * Checks if the case note can be edited based on the date.
     * @param {string} note_date - The date of the case note in 'YYYY-MM-DD' format.
     * @returns {boolean} - Returns true if the case note can be edited, false otherwise.
     */
    function canBeEdited(note_date) {
        if (!note_date || note_date.length !== 10) {
            return true; // Notes without a date or invalid date format can be edited.
        }

        const [year, month, day] = note_date.split("-");
        const noteYear = parseInt(year);
        const noteMonth = parseInt(month);
        const noteDay = parseInt(day);

        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1; // JS months are 0-based
        const currentDay = today.getDate();

        if (currentYear > noteYear ||
            (currentYear === noteYear && currentMonth > (noteMonth + 1)) ||
            (currentYear === noteYear && currentMonth === (noteMonth + 1) && currentDay > 5)) {
            // They can not edit a note written prior to this month, or last month if we are past the 5th of this month.
            return false;
        }
        else {
            // They can edit a note written this month, or last month only up until the 5th of this month.
            return true;
        }
    }

    function deleteCaseNote(/** @type {string} */ id) {
        if (canDelete === true && id.length) {
            deleteId = id;
            confirmDelete = true;
        }
        else {
            toast.error("You do not have permission to delete this case note.");
        }
    }

    function deleteCaseNoteConfirmed() {
        if (canDelete === true && deleteId.length) {
            loading = true;
            dressing.axi.delete(`/cm/case_notes/${deleteId}`, {}).then(
                ({data: response}) => {
                    const {success, data} = response;
                    if (success === true) {
                        fetch_filter_list();
                    }
                    loading = false;
                }).catch((error) => {
                loading = false;
                toast.error("Error deleting case note");
            });
            confirmDelete = false;
            deleteId = "";
        }
    }

    /**
     * @param {string} id
     */
    function editCaseNote(id, cons_id) {
        if (canManage && id.length > 0) {
            selectedNoteId = id;
            selectedConsId = cons_id;
            openCaseNoteModal = true;
        }
    }

    /**
     * @param {string} id
     */
    function pdfCaseNote(id) {
        if (canManage && id.length > 0) {
            window.open(`/index.php?action=${CASE_NOTE_PDF}&id=${id}`, "_blank");
        }
    }

    function createCaseNoteForConsId(/** @type {{value:string, text:string}} */ person) {
        selectedConsId = person.value.length > 0 ? person.value : undefined;
        openCaseNoteModal = true;
    }

    /**
     * Replaces line breaks (\n, \r\n) and tabs (\t) with a single space.
     * Limits the text to a maximum number of characters.
     * Adds "..." if the text is trimmed.
     * @param {string} text
     * @param {number} [maxLength = 100]
     */
    function text_truncate(text, maxLength = 100) {
        // Replace line breaks and tabs with a space
        let cleanedText = text.replace(/[\r\n\t]+/g, " ").trim();

        // Trim if it exceeds maxLength and add "..."
        if (cleanedText.length > maxLength) {
            return cleanedText.substring(0, maxLength).trim() + "...";
        }

        return cleanedText;
    }

    /**
     * @param {string} billable
     * @returns {string} Yes|No|?
     */
    function getBillableText(billable) {
        if (billable === "y") {
            return "Yes";
        }
        else if (billable === "n") {
            return "No";
        }
        else {
            return "?";
        }
    }
</script>

<div transition:fade={{ duration: 350, delay: 150 }}><!-- Breadcrumbs -->
    <Breadcrumb divider={" "} class="breadcrumbs mb-3">
        <BreadcrumbItem>Case Management</BreadcrumbItem>
        <BreadcrumbItem class="mx-3">
            <Icon icon="chevron-right"/>
        </BreadcrumbItem>
        <BreadcrumbItem class="mx-1">
            <Icon icon="briefcase"/>
        </BreadcrumbItem>
        <BreadcrumbItem>{caseNoteName}s</BreadcrumbItem>
    </Breadcrumb>
    <!-- Page Title -->
    <h1>
        <Icon icon="pen-to-square"/>
        {caseNoteName}s
    </h1>
    <!-- Export Buttons -->
    <div class="d-flex w-100 justify-content-end mb-3">
        <ExportButtons
                show={["excel", "pdf"]}
                href="ajax/default/ajax.download_list.php?list={CASE_NOTES_GLOBAL_LIST}"
        />
    </div>

    <!-- TABLE -->
    <FlexList
            {loading}
            pagerConfig={pagination}
            onpagechange={(/** @type {number} */ page) => (pagination.page = page)}
            onsort={(/** @type {string} */ field, /** @type {'asc'|'desc'} */ order) => {
                    sort = { field, order };
                }}
            onpagelengthchange={(/** @type {number} */ newLimit) =>
                (pagination.limit = newLimit)}
            columns={[
                {
                    name: "Person",
                    field: "cons_name",
                    width: "",
                    sortedBy: sort.field === "cons_name" ? sort.order : undefined,
                    sortable: true,
                    collapse: false, // do not hide when in mobile view
                    filterType: "text",
                    filterPlaceholder: "Search",
                    onfilter: function (/** @type {string} */ value) {
                        filterForPerson = value;
                    }
                },
                {
                    name: "Dated",
                    field: "note_date_fmt",
                    width: "",
                    sortedBy: sort.field === "note_date_fmt" ? sort.order : undefined,
                    sortable: true,
                    collapse: false, // do not hide when in mobile view
                    filterType: "text",
                    filterPlaceholder: "Search",
                    onfilter: function (/** @type {string} */ value) {
                        filterForNote_date_fmt = value;
                    },
                },
                {
                    name: "Type",
                    field: "type",
                    width: "",
                    sortable: true,
                    sortedBy: sort.field === "type" ? sort.order : undefined,
                    filterType: "select",
                    filterOptions: caseNoteTypeOptions,
                    onfilter: function (/** @type {string} */ value) {
                        filterForType = value;
                    },
                },
                {
                    name: "Note Preview",
                    field: "note",
                    width: "",
                    cell: toPreview,
                    sortable: true,
                    collapse: false, // do not hide when in mobile view
                    sortedBy: sort.field === "note" ? sort.order : undefined,
                    filterType: "text",
                    filterPlaceholder: "Search",
                    onfilter: function (/** @type {string} */ value) {
                        filterForNote = value;
                    },
                },
                {
                    name: "Billable",
                    field: "billable",
                    width: "90px",
                    cell: billableText,
                    sortable: true,
                    sortedBy: sort.field === "billable" ? sort.order : undefined,
                    filterType: "select",
                    filterOptions: billableTypeOptions,
                    onfilter: function (/** @type {string} */ value) {
                        filterForBillable = value;
                    },
                },
                {
                    name: "Written By",
                    field: "usr_name",
                    width: "",
                    sortable: true,
                    sortedBy: sort.field === "usr_name" ? sort.order : undefined,
                    filterType: "text",
                    filterPlaceholder: "Search",
                    onfilter: function (/** @type {string} */ value) {
                        filterForWritter = value;
                    },
                },
                {
                    name: "Written On",
                    field: "created_fmt",
                    width: "",
                    sortable: true,
                    sortedBy: sort.field === "created_fmt" ? sort.order : undefined,
                    filterType: "text",
                    filterPlaceholder: "Search",
                    onfilter: function (/** @type {string} */ value) {
                        filterForWrittenOn = value;
                    },
                },
                {
                    name: "Actions",
                    width: "110px",
                    buttons: [
                        {
                            icon: "file-pdf",
                            variant: "neutral",
                            tooltip: "Generate PDF",
                            action: function (event, /** @type {CaseNoteListEntry} */ row) {
                                const { id } = row;
                                pdfCaseNote(id);
                            }
                        },
                        {
                            icon: "pencil",
                            tooltip: "Edit",
                            variant: "neutral",
                            action: function (event, /** @type {CaseNoteListEntry} */ row) {
                                const { id, cons_id } = row;
                                editCaseNote(id, cons_id);
                            },
                            disabled: function (/** @type {CaseNoteListEntry} */row) {
                                return !canBeEdited(row.note_date);
                            }
                        },
                        {
                            icon: "times-circle",
                            tooltip: "Delete",
                            variant: "danger",
                            action: function (event, /** @type {CaseNoteListEntry} */ row) {
                                const { id } = row;
                                deleteCaseNote(id);
                            },
                            disabled: function (/** @type {CaseNoteListEntry} */row) {
                                return !(row.allow_edit === "Y" && canDelete);
                            }
                        },
                    ],
                },
                ]}
            expandableRows={true /* Flexlist is expandable for desktops and mobiles */}
            expandableRowsComponent={expansionSnipped}
            data={caseNotes}>
    </FlexList>
</div>

<div class="mb-5">
    <ChoosePersonModal context="case_notes"
                       onselect={createCaseNoteForConsId}
                       confirmButtonText={`Create New ${caseNoteName}`}
                       disabled={!canManage}>
    </ChoosePersonModal>
</div>

<Modal bind:showModal={confirmDelete} title="Confirmation Required" size="sm">
    <div class="modal-body">
        <p>Are you sure you want to delete this note?</p>
        <button class="btn btn-primary me-2" onclick={deleteCaseNoteConfirmed}>
            Delete
        </button>
        <button class="btn btn-secondary" onclick={() => confirmDelete = !confirmDelete}>
            Cancel
        </button>
    </div>
</Modal>

<CaseNoteFormModal
        caseNoteId={selectedNoteId}
        caseNoteConsId={selectedConsId}
        onclose={() => {
            fetch_filter_list();
            selectedNoteId = undefined;
            selectedConsId = undefined;
        }}
        bind:modalOpen={openCaseNoteModal}
/>


{#snippet expansionSnipped(/** @type object */ row, /** @type object */ index)}
    <Card class="m-1">
        <CardHeader>
            <Container>
                <Row cols={{ lg: 4, md: 4, sm: 2, xs: 2 }}>
                    <Col class='fw-bold'>Start Time</Col>
                    <Col>{row['begin_time']}</Col>
                    <Col class='fw-bold'>End Time</Col>
                    <Col>{row['end_time']}</Col>
                    <Col class='fw-bold'>Reason for Contact</Col>
                    <Col>{row['reason']}</Col>
                    <Col class='fw-bold'>Service</Col>
                    <Col>{row['service']}</Col>
                </Row>
            </Container>
        </CardHeader>
        <CardBody>
            {row['note']}
        </CardBody>
    </Card>
{/snippet}

{#snippet toPreview(
    /** @type object */ row,
    /** @type string */ field,
    /** @type object */ column
)}
    {text_truncate(row[field], 40)}
{/snippet}

{#snippet billableText(
    /** @type object */ row,
    /** @type string */ field,
    /** @type object */ column
)}
    {getBillableText(row[field])}
{/snippet}